/**
 * Pure scenario state machines. No DOM, no SDK: the room components dispatch events and render the
 * state, and the verify suite exercises every transition (including the denial paths) in Node.
 *
 * They are deliberately *small*: the authoritative media state stays in `MeetingSession`; these
 * machines own the scenario-specific semantics (call lifecycle, live moderation, 8 mic seats) so
 * that behaviour is testable without a browser.
 */
import type { MessageKey } from '../i18n/messages';

/* ===================================================================================== 1v1 */
export type CallPhase = 'idle' | 'waiting' | 'connected' | 'ended';

export interface OneToOneState {
  phase: CallPhase;
  peerUid: number | null;
  peerName: string | null;
  invited: boolean;
  tokenRenewals: number;
  deviceSwitches: number;
  joinedAt: number | null;
}

export type OneToOneEvent =
  | { type: 'local-joined'; at: number }
  | { type: 'remote-joined'; uid: number; name: string }
  | { type: 'remote-left'; uid: number }
  | { type: 'invite-created' }
  | { type: 'token-renewed' }
  | { type: 'device-switched' }
  | { type: 'ended' };

export function initialOneToOne(): OneToOneState {
  return {
    phase: 'idle',
    peerUid: null,
    peerName: null,
    invited: false,
    tokenRenewals: 0,
    deviceSwitches: 0,
    joinedAt: null,
  };
}

export function reduceOneToOne(state: OneToOneState, event: OneToOneEvent): OneToOneState {
  switch (event.type) {
    case 'local-joined':
      // A late duplicate join must not move a connected call back to waiting.
      if (state.phase === 'connected' || state.phase === 'ended') return state;
      return { ...state, phase: 'waiting', joinedAt: event.at };
    case 'remote-joined':
      // A 1v1 call has exactly one remote party; a second joiner is ignored.
      if (state.peerUid !== null && state.peerUid !== event.uid) return state;
      if (state.phase === 'ended') return state;
      return { ...state, phase: 'connected', peerUid: event.uid, peerName: event.name };
    case 'remote-left':
      if (state.peerUid !== event.uid) return state;
      return {
        ...state,
        phase: state.joinedAt === null ? 'idle' : 'ended',
        peerUid: null,
        peerName: null,
      };
    case 'invite-created':
      return { ...state, invited: true };
    case 'token-renewed':
      return { ...state, tokenRenewals: state.tokenRenewals + 1 };
    case 'device-switched':
      return { ...state, deviceSwitches: state.deviceSwitches + 1 };
    case 'ended':
      return { ...state, phase: 'ended', peerUid: null, peerName: null };
    default:
      return state;
  }
}

export function callStatusKey(state: OneToOneState): MessageKey {
  return state.phase === 'connected' ? '1v1.connected' : '1v1.waiting';
}

/* ===================================================================================== live */
export type LiveRole = 'host' | 'audience';
export type LiveReactionKind = 'heart' | 'clap' | 'like';

export interface LiveComment {
  id: string;
  uid: number;
  name: string;
  text: string;
  at: number;
  kind: 'comment' | 'reaction' | 'system';
}

export interface LiveRoomState {
  localRole: LiveRole;
  hostUid: number | null;
  roles: Record<number, LiveRole>;
  names: Record<number, string>;
  comments: LiveComment[];
  reactions: Record<LiveReactionKind, number>;
  lastReaction: { kind: LiveReactionKind; from: number; name: string; at: number } | null;
}

export type LiveAction =
  | { type: 'join'; localRole: LiveRole; localUid: number; hostUid: number | null }
  | { type: 'comment'; id: string; uid: number; name: string; text: string; at: number }
  | { type: 'reaction'; kind: LiveReactionKind; uid: number; name: string; at: number }
  | { type: 'participant'; uid: number; name: string; role: LiveRole }
  | { type: 'leave'; uid: number }
  | { type: 'moderate'; action: 'mute' | 'kick' | 'set-role'; targetUid: number; role?: LiveRole };

export interface LiveActionResult {
  state: LiveRoomState;
  allowed: boolean;
  /** Localized reason shown as a toast when `allowed` is false. */
  reason: MessageKey | null;
  /** Set-role target role after a successful moderation, used for the toast copy. */
  role: LiveRole | null;
}

export const MAX_LIVE_COMMENTS = 200;

export function initialLiveRoom(localRole: LiveRole = 'host'): LiveRoomState {
  return {
    localRole,
    hostUid: null,
    roles: {},
    names: {},
    comments: [],
    reactions: { heart: 0, clap: 0, like: 0 },
    lastReaction: null,
  };
}

export function isLiveHost(state: LiveRoomState): boolean {
  return state.localRole === 'host';
}

function pushComment(state: LiveRoomState, comment: LiveComment): LiveComment[] {
  return [...state.comments, comment].slice(-MAX_LIVE_COMMENTS);
}

export function reduceLive(state: LiveRoomState, action: LiveAction): LiveActionResult {
  switch (action.type) {
    case 'join':
      return {
        state: {
          ...state,
          localRole: action.localRole,
          hostUid:
            action.hostUid ?? (action.localRole === 'host' ? action.localUid : state.hostUid),
          roles: { ...state.roles, [action.localUid]: action.localRole },
        },
        allowed: true,
        reason: null,
        role: null,
      };
    case 'participant':
      return {
        state: {
          ...state,
          hostUid: action.role === 'host' ? (state.hostUid ?? action.uid) : state.hostUid,
          roles: { ...state.roles, [action.uid]: action.role },
          names: { ...state.names, [action.uid]: action.name },
        },
        allowed: true,
        reason: null,
        role: null,
      };
    case 'leave': {
      const roles = { ...state.roles };
      delete roles[action.uid];
      const names = { ...state.names };
      delete names[action.uid];
      return { state: { ...state, roles, names }, allowed: true, reason: null, role: null };
    }
    case 'comment':
      return {
        state: {
          ...state,
          comments: pushComment(state, {
            id: action.id,
            uid: action.uid,
            name: action.name,
            text: action.text,
            at: action.at,
            kind: 'comment',
          }),
        },
        allowed: true,
        reason: null,
        role: null,
      };
    case 'reaction':
      return {
        state: {
          ...state,
          reactions: { ...state.reactions, [action.kind]: state.reactions[action.kind] + 1 },
          lastReaction: { kind: action.kind, from: action.uid, name: action.name, at: action.at },
          // Reactions are also logged in the comment stream so late joiners see them.
          comments: pushComment(state, {
            id: `r_${action.at}_${action.uid}`,
            uid: action.uid,
            name: action.name,
            text: action.kind,
            at: action.at,
            kind: 'reaction',
          }),
        },
        allowed: true,
        reason: null,
        role: null,
      };
    case 'moderate': {
      if (!isLiveHost(state)) {
        return { state, allowed: false, reason: 'toast.notHost', role: null };
      }
      const targetRole = state.roles[action.targetUid] ?? 'audience';
      if (action.action === 'kick') {
        const roles = { ...state.roles };
        delete roles[action.targetUid];
        const names = { ...state.names };
        delete names[action.targetUid];
        return { state: { ...state, roles, names }, allowed: true, reason: null, role: null };
      }
      if (action.action === 'mute') {
        return { state, allowed: true, reason: null, role: null };
      }
      const nextRole: LiveRole = action.role ?? (targetRole === 'host' ? 'audience' : 'host');
      return {
        state: {
          ...state,
          hostUid: nextRole === 'host' ? action.targetUid : state.hostUid,
          roles: { ...state.roles, [action.targetUid]: nextRole },
        },
        allowed: true,
        reason: null,
        role: nextRole,
      };
    }
    default:
      return { state, allowed: true, reason: null, role: null };
  }
}

/* ===================================================================================== voice */
export const VOICE_SEATS = 8;
export interface VoiceRoomState {
  /** `null` = free seat; a uid = occupied. Length is always {@link VOICE_SEATS}. */
  seatUids: Array<number | null>;
  hands: number[];
  names: Record<number, string>;
  levels: Record<number, number>;
  localRole: LiveRole;
  localUid: number | null;
}

export type VoiceAction =
  | { type: 'join'; localUid: number; localName: string; role: LiveRole }
  | { type: 'participant'; uid: number; name: string; role: LiveRole }
  | { type: 'seat'; uid: number; name: string }
  | { type: 'raise-hand'; uid: number }
  | { type: 'lower-hand'; uid: number }
  | { type: 'approve'; uid: number }
  | { type: 'leave-seat'; uid: number }
  | { type: 'volume'; uid: number; level: number }
  | { type: 'leave'; uid: number };

export interface VoiceActionResult {
  state: VoiceRoomState;
  allowed: boolean;
  reason: MessageKey | null;
  seated: boolean;
}

export function initialVoiceRoom(): VoiceRoomState {
  return {
    seatUids: Array.from({ length: VOICE_SEATS }, () => null),
    hands: [],
    names: {},
    levels: {},
    localRole: 'audience',
    localUid: null,
  };
}

export function seatIndexOf(state: VoiceRoomState, uid: number): number {
  return state.seatUids.findIndex((entry) => entry === uid);
}

export function voiceOccupancy(state: VoiceRoomState): number {
  return state.seatUids.filter((entry) => entry !== null).length;
}

export function isSpeaking(state: VoiceRoomState, uid: number, threshold = 0.05): boolean {
  return (state.levels[uid] ?? 0) > threshold;
}

/** A host always takes a seat; a listener starts off-mic and needs an approved hand. */
export function reduceVoice(state: VoiceRoomState, action: VoiceAction): VoiceActionResult {
  switch (action.type) {
    case 'join': {
      const names = { ...state.names, [action.localUid]: action.localName };
      const seatUids = [...state.seatUids];
      let seated = seatIndexOf({ ...state, seatUids }, action.localUid) >= 0;
      if (action.role === 'host' && !seated) {
        const free = seatUids.findIndex((entry) => entry === null);
        if (free >= 0) {
          seatUids[free] = action.localUid;
          seated = true;
        }
      }
      return {
        state: { ...state, seatUids, names, localRole: action.role, localUid: action.localUid },
        allowed: true,
        reason: null,
        seated,
      };
    }
    case 'participant': {
      const names = { ...state.names, [action.uid]: action.name };
      if (action.role !== 'host' || seatIndexOf(state, action.uid) >= 0) {
        return {
          state: { ...state, names },
          allowed: true,
          reason: null,
          seated: seatIndexOf(state, action.uid) >= 0,
        };
      }
      const seatUids = [...state.seatUids];
      const free = seatUids.findIndex((entry) => entry === null);
      if (free < 0)
        return {
          state: { ...state, names },
          allowed: false,
          reason: 'toast.seatsFull',
          seated: false,
        };
      seatUids[free] = action.uid;
      return { state: { ...state, seatUids, names }, allowed: true, reason: null, seated: true };
    }
    case 'seat': {
      const names = { ...state.names, [action.uid]: action.name };
      if (seatIndexOf(state, action.uid) >= 0) {
        return { state: { ...state, names }, allowed: true, reason: null, seated: true };
      }
      const seatUids = [...state.seatUids];
      const free = seatUids.findIndex((entry) => entry === null);
      if (free < 0)
        return {
          state: { ...state, names },
          allowed: false,
          reason: 'toast.seatsFull',
          seated: false,
        };
      seatUids[free] = action.uid;
      return { state: { ...state, seatUids, names }, allowed: true, reason: null, seated: true };
    }
    case 'raise-hand': {
      if (seatIndexOf(state, action.uid) >= 0) {
        return { state, allowed: true, reason: null, seated: true };
      }
      if (state.hands.includes(action.uid))
        return { state, allowed: true, reason: null, seated: false };
      return {
        state: { ...state, hands: [...state.hands, action.uid] },
        allowed: true,
        reason: null,
        seated: false,
      };
    }
    case 'lower-hand':
      return {
        state: { ...state, hands: state.hands.filter((uid) => uid !== action.uid) },
        allowed: true,
        reason: null,
        seated: false,
      };
    case 'approve': {
      if (state.localRole !== 'host') {
        return { state, allowed: false, reason: 'toast.notHost', seated: false };
      }
      const seatUids = [...state.seatUids];
      if (seatIndexOf(state, action.uid) < 0) {
        const free = seatUids.findIndex((entry) => entry === null);
        if (free < 0) return { state, allowed: false, reason: 'toast.seatsFull', seated: false };
        seatUids[free] = action.uid;
      }
      return {
        state: { ...state, seatUids, hands: state.hands.filter((uid) => uid !== action.uid) },
        allowed: true,
        reason: null,
        seated: true,
      };
    }
    case 'leave-seat': {
      const seatUids = state.seatUids.map((entry) => (entry === action.uid ? null : entry));
      return { state: { ...state, seatUids }, allowed: true, reason: null, seated: false };
    }
    case 'volume': {
      const levels = { ...state.levels, [action.uid]: action.level };
      return {
        state: { ...state, levels },
        allowed: true,
        reason: null,
        seated: seatIndexOf(state, action.uid) >= 0,
      };
    }
    case 'leave': {
      const names = { ...state.names };
      delete names[action.uid];
      const levels = { ...state.levels };
      delete levels[action.uid];
      return {
        state: {
          ...state,
          seatUids: state.seatUids.map((entry) => (entry === action.uid ? null : entry)),
          hands: state.hands.filter((uid) => uid !== action.uid),
          names,
          levels,
        },
        allowed: true,
        reason: null,
        seated: false,
      };
    }
    default:
      return { state, allowed: true, reason: null, seated: false };
  }
}

/** Stable seat descriptor for the voice grid (occupied or not). */
export function describeSeat(
  state: VoiceRoomState,
  index: number,
): { index: number; uid: number | null; name: string | null } {
  const uid = state.seatUids[index] ?? null;
  return { index, uid, name: uid === null ? null : (state.names[uid] ?? `User ${uid}`) };
}

/* ===================================================================================== React glue */
/** `useReducer`-shaped wrappers; imperative callers use {@link reduceLive}/{@link reduceVoice}
 *  directly to inspect the `allowed`/`reason` denial envelope. */
export function liveReducer(state: LiveRoomState, action: LiveAction): LiveRoomState {
  return reduceLive(state, action).state;
}

export function voiceReducer(state: VoiceRoomState, action: VoiceAction): VoiceRoomState {
  return reduceVoice(state, action).state;
}
